// TODO(RBAC, parent #641 #647): 権限チェックは別途実装
import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router'

import { Link, PrimaryButton } from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { DeleteCaseDialog } from '~/components/common/DeleteCaseDialog'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import {
  useWorkflowAutomationAvailability,
  workflowAutomationGate,
} from '~/hooks/useWorkflowAutomationAvailability'
import { useListBgMasters } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { CaseTable } from '~/pages/WorkflowCases/components/CaseTable'
import { CreateWorkflowCasesDialog } from '~/pages/WorkflowCases/components/CreateWorkflowCasesDialog'
import {
  DEFAULT_FILTER_STATE,
  FilterBar,
} from '~/pages/WorkflowCases/components/FilterBar'
import type { FilterBarState } from '~/pages/WorkflowCases/components/FilterBar'
import { ListEmptyPanel } from '~/pages/WorkflowCases/components/ListEmptyPanel'
import { useListSubmitterMastersForBadge } from '~/pages/WorkflowCases/hooks/useListSubmitterMastersForBadge'
import type { WorkflowCasesFilter } from '~/pages/WorkflowCases/hooks/useListWorkflowCases'
import { useListWorkflowCases } from '~/pages/WorkflowCases/hooks/useListWorkflowCases'
import {
  WORKFLOW_CASES_FILTERS_STORAGE_KEY,
  workflowCasesFiltersCodec,
} from '~/pages/WorkflowCases/models/persistedFilters'
import type {
  SortBy,
  SortOrder,
} from '~/pages/WorkflowCases/models/sortWorkflowCases'
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  sortWorkflowCases,
} from '~/pages/WorkflowCases/models/sortWorkflowCases'
import type { WorkflowCaseRow } from '~/pages/WorkflowCases/models/workflowCase'
import { buildWorkflowTargetCodeKindByCode } from '~/pages/WorkflowCases/models/workflowCase'
import { deriveWorkflowCaseStatuses } from '~/pages/WorkflowCases/models/workflowCaseFilter'
import { buildWorkflowTargetCodeFilterOptions } from '~/pages/WorkflowCases/models/workflowTargetCodeFilterOptions'
import { routePaths } from '~/routes/routePaths'

/**
 * URL クエリパラメータ ?sortBy=progress|targetDate|updatedAt&sortOrder=asc|desc を
 * ソート状態に変換する (spec AC-WFW-001: 未指定・不正値はデフォルト = 最終更新降順)。
 */
function parseSortByParam(value: string | null): SortBy {
  switch (value) {
    case 'progress':
    case 'targetDate':
    case 'updatedAt':
      return value
    default:
      return DEFAULT_SORT_BY
  }
}

function parseSortOrderParam(value: string | null): SortOrder {
  return value === 'asc' ? 'asc' : DEFAULT_SORT_ORDER
}

export const WorkflowCasesPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [deletingRow, setDeletingRow] = useState<WorkflowCaseRow | undefined>(
    undefined,
  )

  // FilterBar state (section tab + status segment/checkbox/area/bg select)
  const { filters: filterState, updateFilters } =
    usePersistedFilters<FilterBarState>({
      storageKey: WORKFLOW_CASES_FILTERS_STORAGE_KEY,
      codec: workflowCasesFiltersCodec,
    })
  const setFilterState = (next: FilterBarState): void =>
    updateFilters(() => next)

  /**
   * フィルタが 1 つでも既定から動いているか。 空状態の文言と glyph の出し分けに使う。
   *
   * **件数ではなく「操作されたか」で判定する。** 0 件の理由が「まだ作っていない」 なのか
   * 「絞り込んだ結果」 なのかは件数からは区別できず、 後者に「追加してください」 と出すと
   * 誤った次の行動を示す。
   */
  const isFiltered = (
    Object.keys(DEFAULT_FILTER_STATE) as Array<keyof FilterBarState>
  ).some(
    (k) =>
      JSON.stringify(filterState[k]) !==
      JSON.stringify(DEFAULT_FILTER_STATE[k]),
  )

  // ソート状態は URL search params が SoT (spec AC-WFW-001: ページ遷移・再訪時にも維持)。
  // localStorage は共有不可、React state は遷移で消えるため不適合 (設計書 §4)。
  const [searchParams, setSearchParams] = useSearchParams()
  const sortBy = parseSortByParam(searchParams.get('sortBy'))
  const sortOrder = parseSortOrderParam(searchParams.get('sortOrder'))
  const handleSortByChange = (
    nextSortBy: SortBy,
    nextSortOrder: SortOrder,
  ): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('sortBy', nextSortBy)
      next.set('sortOrder', nextSortOrder)
      return next
    })
  }

  // BFF への request filter (BG は backend に絞り込み field が無いため client-side filter、
  // 下記 filteredRows 参照)。AC-05: statuses は segment + checkbox 2 個から導出する。
  // AC-04/AC-06: エリアは repeated area_codes field をそのまま渡す (server-side filter)。
  const bffFilter: WorkflowCasesFilter = useMemo(
    () => ({
      section: filterState.section !== null ? filterState.section : undefined,
      statuses: deriveWorkflowCaseStatuses(
        filterState.statusSegment,
        filterState.showComplete,
        filterState.showDeleted,
      ),
      areaCodes: filterState.areaCodes,
      // AC-WFW-001 機能詳細: 対象日期間フィルタを BFF/List RPC に渡す。
      targetDateFrom: filterState.targetDateFrom || undefined,
      targetDateTo: filterState.targetDateTo || undefined,
      // workflow-list-action-columns AC-07、spec §機能詳細のフィルタ
      // (workflow.md §415): 「あなたが対応すべきもののみ」も server-side
      // filter (viewer は BFF が eauth から注入する)。他フィルタとは AND
      // で合成。
      onlyViewerActionRequired: filterState.onlyViewerActionRequired,
    }),
    [
      filterState.section,
      filterState.statusSegment,
      filterState.showComplete,
      filterState.showDeleted,
      filterState.areaCodes,
      filterState.targetDateFrom,
      filterState.targetDateTo,
      filterState.onlyViewerActionRequired,
    ],
  )

  const { rows, isLoading, data } = useListWorkflowCases(bffFilter)

  // spec AC-001 機能詳細: BG フィルタは BFF request field が無いため client-side filter。
  // AC-05/AC-06/AC-07: bgCodes は複数選択 (chip 式)、AND 条件で area (server-side) と合成される。
  // AC-09: タグフィルタも同じ理由で client-side (FilterBar.tsx コメント参照、OR 条件)。
  const filteredRows = useMemo(
    () =>
      rows
        .filter((row) =>
          filterState.bgCodes.length === 0
            ? true
            : // BG なしケース (bgCode 未設定) は特定 BG での絞り込みには
              // 一致しない (= BG 軸を持たないため)。
              row.bgCode !== undefined &&
              filterState.bgCodes.includes(row.bgCode),
        )
        .filter((row) =>
          filterState.tagLabels.length === 0
            ? true
            : row.tags.some((tag) => filterState.tagLabels.includes(tag.label)),
        ),
    [rows, filterState.bgCodes, filterState.tagLabels],
  )

  // AC-WFW-001: 一覧のデフォルトソート (client-side)。
  const sortedRows = useMemo(
    () => sortWorkflowCases(filteredRows, sortBy, sortOrder),
    [filteredRows, sortBy, sortOrder],
  )

  // AC-09: タグフィルタの候補一覧 (取得済み rows から導出、重複無し・出現順)
  const tagOptions = useMemo(() => {
    const seen = new Set<string>()
    const labels: string[] = []
    for (const row of rows) {
      for (const tag of row.tags) {
        if (!seen.has(tag.label)) {
          seen.add(tag.label)
          labels.push(tag.label)
        }
      }
    }
    return labels
  }, [rows])

  // BG マスタ一覧 (AC-05 BG フィルタの候補 + AC-02 License バッジ突合の両方で使う)
  const { data: bgMastersData } = useListBgMasters()
  const bgMasterItems = useMemo(
    () => bgMastersData?.items ?? [],
    [bgMastersData?.items],
  )
  // 計画提出者マスタは「発電」バッジの突合と BG フィルタ候補に使う (AC-WFW-001)。
  const { data: submitterMastersData, isError: isSubmitterMastersError } =
    useListSubmitterMastersForBadge()
  const bgFilterOptions = useMemo(
    () =>
      buildWorkflowTargetCodeFilterOptions(
        bgMasterItems,
        submitterMastersData?.items ?? [],
      ),
    [bgMasterItems, submitterMastersData?.items],
  )
  const targetCodeKindByCode = useMemo(
    () =>
      buildWorkflowTargetCodeKindByCode(
        bgMasterItems,
        submitterMastersData?.items ?? [],
      ),
    [bgMasterItems, submitterMastersData?.items],
  )

  // 列 0「起票」は自動起票ルールと同じ flag で出す (AC-WFW-001 ※)。3 状態 gate は
  // 再取得中に直前の値を保つので、window focus の再取得で列が消えて戻らない。
  // pending・取得失敗は列を出さない (fail-closed)。
  const showCreatedVia =
    workflowAutomationGate(useWorkflowAutomationAvailability()) === 'on'

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('common:nav.workflow') },
        {
          label: t('common:route.workflowCases'),
          bold: true,
        },
      ]}
    >
      <PageBody>
        {/* cases-header: 説明文 + ワークフロー追加ボタン */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <PageIntro
            description={
              <>
                {t('page:workflowCases.description')}{' '}
                <Link asChild>
                  <RouterLink to={routePaths.adminWorkflowTemplates.path}>
                    {t('page:workflowCases.descriptionLinkLabel')}
                  </RouterLink>
                </Link>
                {t('page:workflowCases.descriptionSuffix')}
              </>
            }
          />
          {/* TODO(RBAC, parent #641 #647): 新規作成は権限チェック後に制限 */}
          <PrimaryButton
            flexShrink={0}
            leftIcon={<FaPlusRegular />}
            onClick={() => {
              setIsCreateDialogOpen(true)
            }}
          >
            {t('page:workflowCases.action.create')}
          </PrimaryButton>
        </div>

        <FilterBar
          bgOptions={bgFilterOptions}
          filter={filterState}
          onChange={setFilterState}
          tagOptions={tagOptions}
        />
        {isSubmitterMastersError && (
          <CompactAlert
            status="warning"
            title={t('page:workflowCases.filter.bgOptionsLoadError')}
          />
        )}

        {/* テーブル */}
        <RegionLoading
          flexDirection="column"
          flex="1"
          display="flex"
          minHeight={isLoading && data === undefined ? '200px' : 0}
          isLoading={isLoading && data === undefined}
        >
          <PageScrollArea>
            {isLoading && data === undefined ? null : sortedRows.length ===
              0 ? (
              /* 空状態は理由で描き分ける (`empty-panel.md`「いつ使うか」)。
               フィルタ未適用 = まだ作られていない / 適用中 = 絞り込んだ結果 0 件。
               次の行動が違うので Alert 1 本ではなくパネルにする。 */
              <ListEmptyPanel isFiltered={isFiltered} />
            ) : (
              /* 枠線・角丸・クリップは MasterDataTable 自身が持つ
               (borderWidth 1px / Border.lowEmphasis / borderRadius md + overflow auto)。
               ここで重ねて囲うと外周が二重線になっていた (2026-08-17 デザイナー指摘)。 */
              <CaseTable
                onDeleteClick={setDeletingRow}
                onSortByChange={handleSortByChange}
                rows={sortedRows}
                showCreatedVia={showCreatedVia}
                sortBy={sortBy}
                sortOrder={sortOrder}
                targetCodeKindByCode={targetCodeKindByCode}
              />
            )}
          </PageScrollArea>
        </RegionLoading>
      </PageBody>

      {/* ワークフロー追加ダイアログ (N×M 一括作成) */}
      <CreateWorkflowCasesDialog
        onClose={() => {
          setIsCreateDialogOpen(false)
        }}
        open={isCreateDialogOpen}
      />

      {/* 削除確認ダイアログ (spec AC-003f/g、workflow-delete-transition design AC-01/AC-02) */}
      <DeleteCaseDialog
        row={deletingRow}
        onClose={() => {
          setDeletingRow(undefined)
        }}
      />
    </PageLayout>
  )
}
